import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { selectLanguage } from 'src/state/app-meta/app-meta.selectors';
import { ConfigApiService } from './config-api.service';
import { LoadConfigService } from './load-config.service';
import { ExtractedMediaFile } from './media-loader.service';
import { ReloadingService } from './reloading.service';
import { StateService } from './state.service';
import { StudyNavigationService } from './study-navigation.service';

@Injectable({
  providedIn: 'root'
})
export class ArchiveDNSResultsService {
  //TODO:#type
  private clientConfig: object;
  studyResults = {};

  private archivedResults = {};

  constructor(
    private navigationService: StudyNavigationService,
    private stateService: StateService,
    private reloadingService: ReloadingService,
    private configService: LoadConfigService,
    private configApiService: ConfigApiService,
    private store: Store
  ) {
    this.clientConfig = this.configService.getClientConfig();
    this.init();
  }

  private loadArchivedResults(state) {
    if (!state) {
      return;
    }

    // this is necessary as the simple statement (state && state['result']) returns true even if the state['results'] object is empty
    const hasPreviousResults =
      Object.keys(state).length > 0 &&
      state.constructor === Object &&
      Object.keys(state['results']).length > 0 &&
      state['results'].constructor === Object;

    if (hasPreviousResults) {
      this.setArchivedResults(state['results']);
    }

    const resetAfterReload = this.configService.getConfig()['resetResultsAfterReload'];

    if (resetAfterReload) {
      this.resetLastIATResults();
    }
  }

  private resetLastIATResults() {
    const { title } = this.navigationService.getCurrentPosition();
    if (title.endsWith('test')) {
      const currentIATconfig = this.navigationService.getConfig();
      const { blockNr, type, mode } = currentIATconfig;
      const lastIAT = this.archivedResults['IAT'][this.archivedResults['IAT'].length - 1];

      const reloadedDuringIAT =
        lastIAT !== undefined &&
        lastIAT['blockID'] === blockNr &&
        lastIAT['type'] === type &&
        lastIAT['IATNr'] === this.navigationService.getTestId() &&
        lastIAT['mode'] === mode;

      /* 
        have to make sure that last IAT is the same as the current IAT in order to reset / pop the results; otherwise we might end up deleting the results of the previous IAT (happens if the user reload when seeing the first stimulus without making a decision)
      */
      if (reloadedDuringIAT) {
        const lastIATResults = this.archivedResults['IAT'].pop();

        const currentResetCounter = lastIATResults['resetCounter'];
        /* 
          this will be called BEFORE initialization of the IAT template for the current IAT. Results is no knowledge of the resetCounter that we pop when initializing the new replacing IAT template.
        */
        const resultTemplate = this.getResultTemplate(
          lastIATResults['IATNr'],
          lastIATResults['blockID'],
          lastIATResults['mode'],
          lastIATResults['type'],
          lastIATResults['description'],
          currentResetCounter + 1,
          lastIATResults['brandName'],
          lastIATResults['brandImage']
        );

        resultTemplate['id'] = lastIATResults['id'];

        this.archivedResults['IAT'].push(resultTemplate);
      }
    }
  }

  private setArchivedResults(results) {
    this.archivedResults = results;
  }

  private init() {
    this.store.select(selectLanguage).subscribe((lng: string) => {
      this.initializeArchivedResults(lng);
    });
    this.loadArchivedResults(this.reloadingService.getState());
    this.stateService.setState(this.archivedResults, 'results');
  }

  getArchivedResults() {
    return this.archivedResults;
  }

  private initializeArchivedResults(lng: string) {
    this.archivedResults['questionnaire'] = this.initializeArchivedQuestionnaire(lng) || [];

    this.archivedResults['questionConfig'] = JSON.parse(JSON.stringify(this.clientConfig['questions']));

    this.archivedResults['IAT'] = [];
    this.archivedResults['IATConfig'] = JSON.parse(JSON.stringify(this.configService.getStudiesInfo()));

    this.archivedResults['media'] = [];
  }

  private getQuestionAnswerTemplate(questionID, questionNr, question) {
    return {
      questionID,
      questionNr,
      question,
      answer: [],
      shown: false,
      parentQuestions: [],
      childQuestions: []
    };
  }

  private initializeArchivedQuestionnaire(lng = 'EN') {
    const questionsJSON = this.clientConfig['questions'][lng];
    if (!questionsJSON) return undefined; // There is no question.

    const mergedQuestions = questionsJSON
      .reduce((acc, value) => acc.concat(value), [])
      .map((questionJSON, idx) => {
        return {
          ...questionJSON,
          questionNr: idx + 1
        };
      });

    // assumes child questions are 'flat'
    const questionsTemplate = mergedQuestions.map(question =>
      this.getQuestionAnswerTemplate(question.questionID, question.questionNr, question.question)
    );

    return questionsTemplate;
  }

  private getDecisionTemplateForMode(mode: string, IATtype: string) {
    if (mode === 'reading') {
      return {
        continue: []
      };
    }

    const isPracticeOrIAT = mode === 'practice' || mode === 'IAT';

    switch (IATtype) {
      case 'persona':
      case 'brand':
      case 'modular':
      case 'IAT':
      case 'conjoint':
      case 'priming': {
        if (isPracticeOrIAT) {
          return {
            0: [],
            1: []
          };
        }
        break;
      }
      case 'agreement': {
        if (isPracticeOrIAT) {
          return {
            0: [],
            1: [],
            2: [],
            3: []
          };
        }
        break;
      }
      case 'oldMechanic': {
        if (isPracticeOrIAT) {
          return {
            selected: []
          };
        }
      }
    }
  }

  initializeArchivedIATResults(IATInfo: {
    mode: string;
    IATtype: string;
    blockID: number;
    brandImage?: string;
    brandName?: string;
  }) {
    const IATNr = this.navigationService.getTestId();
    const currentIATConfig = this.configService.getRandomizedTestConfigByIdx(IATNr);

    const { blockID, IATtype, mode, brandImage, brandName } = IATInfo;
    const description = currentIATConfig['description'];

    const entryForIATExists =
      this.archivedResults['IAT'].length !== 0 &&
      this.archivedResults['IAT'][this.archivedResults['IAT'].length - 1]['blockID'] === blockID;

    const resetAfterReload = this.configService.getConfig()['resetResultsAfterReload'];

    if (!entryForIATExists || !resetAfterReload) {
      let resetCounter = 0;

      if (entryForIATExists) {
        const lastIAT = this.archivedResults['IAT'][this.archivedResults['IAT'].length - 1];
        resetCounter = lastIAT['resetCounter'] + 1;
      }

      const resultTemplate = this.getResultTemplate(
        IATNr,
        blockID,
        mode,
        IATtype,
        description,
        resetCounter,
        brandName,
        brandImage
      );

      resultTemplate['id'] = currentIATConfig['id'];

      this.archivedResults['IAT'].push(resultTemplate);
      this.saveResultsToState();
    }
  }

  private getResultTemplate(IATNr, blockID, mode, IATtype, description, resetCounter, brandName, brandImage) {
    const resultTemplate = {
      IATNr,
      blockID,
      mode,
      type: IATtype,
      description,
      decision: this.getDecisionTemplateForMode(mode, IATtype),
      resetCounter: resetCounter
    };

    const currentIATConfig = this.configService.getRandomizedTestConfigByIdx(IATNr);

    if (IATtype === 'brand') {
      resultTemplate['brandName'] = brandName;
      resultTemplate['brandImage'] = brandImage;
    }

    if (IATtype === 'IAT' || IATtype === 'priming') {
      resultTemplate['stimuliPositions'] = currentIATConfig['stimuliPositions'];
      resultTemplate['stimuliRepetitions'] = currentIATConfig['stimuliRepetitions'];
    }

    return resultTemplate;
  }

  /*
    This method is used for the actual IAT type studies; 'saveIATDecision' for the other types
    naming evolved as the studies for initially called IATs;
    the initial project of the new type was needed for was called Future of Trust (FoT); hence the name
  */
  saveIATDecisionFoT(info: {
    IATtype: string;
    mode: string;
    stimulus: { value: string; type: string };
    reactionTimeUntilFirstDecision: number;
    reactionTimeUntilCorrectDecision: number;
    madeCorrectDecision: boolean;
    receivedUserInput: boolean;
  }) {
    const currentIAT = this.archivedResults['IAT'][this.archivedResults['IAT'].length - 1];

    const {
      IATtype,
      mode,
      stimulus,
      reactionTimeUntilFirstDecision,
      reactionTimeUntilCorrectDecision,
      madeCorrectDecision,
      receivedUserInput
    } = info;

    if (IATtype !== 'IAT') {
      throw new Error('cannot save non-IAT type studies');
    }

    if (mode === 'reading') {
      const decisionsOfCurrentIAT = currentIAT['decision']['continue'];
      const decisionInfo = {
        stimulus,
        reactionTime: reactionTimeUntilCorrectDecision,
        madeDecision: receivedUserInput,
        timeOfDecision: Date.now()
      };
      decisionsOfCurrentIAT.push(decisionInfo);
    } else {
      const mappedDecision = madeCorrectDecision ? 1 : 0;
      const decisionsOfCurrentIAT = currentIAT['decision'][mappedDecision];
      const decisionInfo = {
        stimulus: stimulus.value,
        category: stimulus.type,
        reactionTimeUntilFirstDecision,
        reactionTimeUntilCorrectDecision,
        madeCorrectDecision,
        madeDecision: receivedUserInput,
        timeOfDecision: Date.now()
      };

      decisionsOfCurrentIAT.push(decisionInfo);
    }

    this.saveResultsToState();
  }

  /* Not yet needed for CIP: Save priming data. 
     Used Priming component -> Do no remove here, unless 
     Priming component is also removed. */
  savePrimingDecision(info: {
    IATtype: string;
    mode: string;
    stimulus: { value: string; type: string };
    reactionTimeUntilFirstDecision: number;
    reactionTimeUntilCorrectDecision: number;
    madeCorrectDecision: boolean;
    receivedUserInput: boolean;
  }) {
    const currentIAT = this.archivedResults['IAT'][this.archivedResults['IAT'].length - 1];

    const {
      IATtype,
      mode,
      stimulus,
      reactionTimeUntilFirstDecision,
      reactionTimeUntilCorrectDecision,
      madeCorrectDecision,
      receivedUserInput
    } = info;

    if (IATtype !== 'priming') {
      throw new Error('cannot save non-priming type studies');
    }

    if (mode === 'reading') {
      const decisionsOfCurrentIAT = currentIAT['decision']['continue'];
      const decisionInfo = {
        stimulus,
        reactionTime: reactionTimeUntilCorrectDecision,
        madeDecision: receivedUserInput,
        timeOfDecision: Date.now()
      };
      decisionsOfCurrentIAT.push(decisionInfo);
    } else {
      const mappedDecision = madeCorrectDecision ? 1 : 0;
      const decisionsOfCurrentIAT = currentIAT['decision'][mappedDecision];
      const decisionInfo = {
        stimulus: stimulus.value,
        category: stimulus.type,
        reactionTimeUntilFirstDecision,
        reactionTimeUntilCorrectDecision,
        madeCorrectDecision,
        madeDecision: receivedUserInput,
        timeOfDecision: Date.now()
      };

      decisionsOfCurrentIAT.push(decisionInfo);
    }

    this.saveResultsToState();
  }

  saveIATDecision(info: {
    IATtype: string;
    mode: string;
    stimulus: string;
    shownStimuli?: string[] | number[];
    reactionTime: number;
    decision: string | number;
    receivedUserInput: boolean;
  }) {
    const currentIAT = this.archivedResults['IAT'][this.archivedResults['IAT'].length - 1];

    const { IATtype, mode, stimulus, shownStimuli, reactionTime, decision, receivedUserInput } = info;

    const decisionsOfCurrentIAT = currentIAT['decision'][decision];
    const decisionInfo = {
      stimulus,
      reactionTime,
      madeDecision: receivedUserInput,
      timeOfDecision: Date.now()
    };

    if (['persona', 'brand', 'agreement', 'modular'].includes(IATtype)) {
      decisionsOfCurrentIAT.push(decisionInfo);
    } else if (IATtype === 'oldMechanic') {
      if (mode === 'reading') {
        decisionsOfCurrentIAT.push(decisionInfo);
      } else {
        decisionsOfCurrentIAT.push({
          ...decisionInfo,
          shownStimuli
        });
      }
    }
    this.saveResultsToState();
  }

  /* Not yet needed for CIP: Save conjoint data.
     Used Conjoint component -> Do no remove here, unless 
     Conjoint component is also removed. */
  saveConjointDecision(info: {
    IATtype: string;
    mode: string;
    stimulus: string;
    decision: string | number;
    receivedUserInput: boolean;
  }) {
    const currentIAT = this.archivedResults['IAT'][this.archivedResults['IAT'].length - 1];

    const { IATtype, stimulus, decision, receivedUserInput } = info;

    //currentIAT['decision'] = decision;
    if (IATtype !== 'conjoint') {
      throw new Error('cannot save non-Conjoint type studies');
    }

    const decisionsOfCurrentIAT = currentIAT['decision'][decision];
    const decisionInfo = {
      stimulus,
      decision,
      madeDecision: receivedUserInput,
      timeOfDecision: Date.now()
    };

    decisionsOfCurrentIAT.push(decisionInfo);

    this.saveResultsToState();
  }

  // TODO: empty answers results in an answer array of [null], should be []
  // parent question IDs are not saved as this can be matched in the backend / analysis scripts
  saveQuestionnaireAnswers(questionnaire: FormGroup) {
    const questionnaireValues = questionnaire.value;

    for (const question of Object.keys(questionnaireValues)) {
      const formGroup = questionnaireValues[question];
      const questionID = formGroup['questionID'];
      const questionNr = formGroup['questionNr'];

      const questionTemplate = this.archivedResults['questionnaire'].find(
        template => template['questionID'] == questionID && template['questionNr'] == questionNr
      );

      questionTemplate['shown'] = formGroup['isVisible'];

      if (!questionTemplate['shown']) continue;

      // question is questionTitle + '_Group'
      const questionTitle = question.substring(0, question.length - 6);
      // TODO#code
      // eslint-disable-next-line no-prototype-builtins
      if (formGroup.hasOwnProperty(`${questionTitle}_Array`)) {
        const answers = formGroup[`${questionTitle}_Array`];
        questionTemplate['answer'] = answers;
      } else {
        questionTemplate['answer'] = [formGroup[questionTitle]];
      }
    }

    this.saveResultsToState();
  }

  saveMediaToResults(media: ExtractedMediaFile, readingTime: number) {
    this.archivedResults['media'].push({
      ...media,
      readingTime
    });
    this.saveResultsToState();
  }

  saveResultsToState() {
    this.stateService.setState(this.archivedResults, 'results');
  }

  /* Sascha: Deprecated: To be removed
  saveUserData(params) {
    const { country, lng: language, id, uid } = params;

    this.writeResults(
      {
        country,
        language,
        id,
        uid,
      },
      this.archivedResults,
      'user'
    );
    this.saveResultsToState();
  } */

  saveConsent() {
    this.archivedResults['consent'] = 'agree';
    const user = {
      user_data: {
        country: this.configApiService.getParams().country,
        language: this.configApiService.getParams().lng,
        user_id: this.configApiService.getParams().user,
        study_id: this.configApiService.getParams().id
      }
    };
    this.archivedResults['user'] = user;
    this.saveResultsToState();
  }

  private writeResults(source: object, target: object, name: string): void {
    target[name] = source;
  }
}
